import { Skeleton, SkeletonGroup } from '@shared/components/Skeleton';
import { Box, ButtonV2, Divider, Flex, TextV2 } from '@withjoy/joykit';
import React from 'react';
import { GeneralEventScheduleData, GeneralEventScheduleDataDay } from '../schedule.types';
import { split86012String } from '@shared/components/ScheduleEditor/components/form/hooks/useGetEventTimezone';
import { DayGroup } from './DayGroup';
import { EventRow } from './EventRow';
import { ConfirmationDialogMode } from './ConfirmationDialog';
import { TbaSection } from './TbaSection';
import { useTranslation } from '@shared/core';

type DayToggleProps = {
  day: GeneralEventScheduleDataDay;
  isActive: boolean;
  setIsActive: (state: boolean) => void;
  onEditEvent: (eventId: string) => void; // Callback for editing an event
  onAddClick: () => void;
  setConfirmationType: (type: ConfirmationDialogMode | null) => void;
  setDeleteId: (id: string) => void;
};

const getEventSubtitle = (event: { startTimeISO8601TZ?: string | null; endTimeISO8601TZ?: string | null }) => {
  const startDate = event.startTimeISO8601TZ ? split86012String(event.startTimeISO8601TZ).datetime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : null;
  const endDate = event.endTimeISO8601TZ ? split86012String(event.endTimeISO8601TZ).datetime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : null;

  if (endDate) {
    return `${startDate} - ${endDate}`;
  }
  return startDate || '';
};

export const DayToggle = (props: DayToggleProps) => {
  const { day, onEditEvent, onAddClick, setConfirmationType, setDeleteId } = props;
  const date = split86012String(day.dateISO8601TZ).datetime;
  const { tq } = useTranslation('generalEventScheduleEditor');

  const formattedDate = date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <DayGroup
      title={formattedDate}
      onAddClick={onAddClick}
      subtitle={tq`Event Privacy`}
    >
      {day.items.map((event, index) => {
        const subtitle = getEventSubtitle(event);
        const showDivider = index !== day.items.length - 1;

        return (
          <>
            <EventRow
              onEditClick={() => {
                onEditEvent(event.id);
              }}
              key={event.id}
              isPublic={true}
              title={event.name}
              subtitle={subtitle}
              onDeleteClick={() => {
                setConfirmationType('delete');
                setDeleteId(event.id);
              }}
            ></EventRow>
            {showDivider && <Divider />}
          </>
        );
      })}
    </DayGroup>
  );
};

const DayFilterSkeleton = () => {
  return (
    <Flex
      flexDirection="column"
      gap="1rem"
    >
      <Skeleton
        width="416px"
        height="86px"
        borderRadius="5px"
      />
      <Skeleton
        width="416px"
        height="86px"
        borderRadius="5px"
      />
      <Skeleton
        width="416px"
        height="86px"
        borderRadius="5px"
      />
    </Flex>
  );
};

type DayFilterProps = {
  excludedDays: string[];
  setExcludedDays: (newValue: string[]) => void;
  onEditEvent: (eventId: string) => void;
  onAddEvent: () => void;
  schedule: GeneralEventScheduleData | null;
  setConfirmationType: (type: ConfirmationDialogMode | null) => void;
  setDeleteId: (id: string) => void;
};

export const DayFilter = (props: DayFilterProps) => {
  const { excludedDays, setExcludedDays, onEditEvent, onAddEvent, setConfirmationType, schedule, setDeleteId } = props;
  const totalCount = (schedule?.formattedSchedules?.length ?? 0) + (schedule?.tbaSchedules?.length ?? 0);
  const { tq } = useTranslation('generalEventScheduleEditor');

  const handleSetIsActive = (timestamp: string, isActive: boolean) => {
    if (!isActive) {
      setExcludedDays([...excludedDays, timestamp]);
    } else {
      setExcludedDays(excludedDays.filter(t => t !== timestamp));
    }
  };

  if (totalCount === 0) {
    return (
      <Flex
        width="100%"
        height="100%"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        padding="24px"
      >
        <TextV2
          typographyVariant="hed4"
          textAlign="center"
          marginBottom="1rem"
        >
          {tq`Start building your schedule`}
        </TextV2>
        <ButtonV2
          shape="rounded"
          intent="primary"
          onClick={onAddEvent}
        >
          {tq`Add Event`}
        </ButtonV2>
      </Flex>
    );
  }

  return (
    <Box marginTop="1rem">
      <SkeletonGroup
        placeholder={<DayFilterSkeleton />}
        isReady={!!schedule}
      >
        <Flex
          flexDirection="column"
          gap="40px"
        >
          {!!schedule?.tbaSchedules.length && (
            <TbaSection
              onAddClick={onAddEvent}
              day={schedule.tbaSchedules}
              onEditEvent={onEditEvent}
              setConfirmationType={setConfirmationType}
              setDeleteId={setDeleteId}
            />
          )}
          {(schedule?.formattedSchedules ?? []).map(day => {
            const date = split86012String(day.dateISO8601TZ);
            const isActive = !excludedDays.includes(date.datetime.toISOString());

            return (
              <DayToggle
                key={date.datetime.toISOString()}
                day={day}
                isActive={isActive}
                setIsActive={state => handleSetIsActive(date.datetime.toISOString(), state)}
                onEditEvent={onEditEvent} // Pass the callback to DayToggle
                onAddClick={onAddEvent}
                setConfirmationType={setConfirmationType}
                setDeleteId={setDeleteId}
              />
            );
          })}
        </Flex>
      </SkeletonGroup>
    </Box>
  );
};
